import {
  BREAKPOINTS_V3,
  ComparisonSlider,
  GridItem,
  LayoutGrid,
  TypographyV3,
  useMediaQuery,
} from '@jouzen/ecom-components';
import { useInView } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';

import Motion from '@/app/components/Motion';
import SectionHeading from '@/app/components/SectionHeading';

import { PopStyleComparisonItem } from './PopStyleComparisonItem';

export function PopStyleComparison(): JSX.Element {
  const t = useTranslations();
  const matchLargeScreen = useMediaQuery(
    `(min-width: ${BREAKPOINTS_V3.large}px)`,
  );
  const sliderRef = useRef<HTMLDivElement>(null);
  const sliderIsInView = useInView(sliderRef, { amount: 0.75, once: true });

  return (
    <section className="relative py-14 lg:pb-16 lg:pt-24">
      <LayoutGrid>
        <GridItem colEnd={{ sm: 'main' }} colStart={{ sm: 'main' }}>
          <div className="md:flex md:justify-center">
            <SectionHeading
              animated
              content="pop_style_comparison_title"
              indentation="pl-48"
              multiline={!matchLargeScreen}
              typographyProps={{
                className:
                  'text-heading-2xl leading-heading mb-6 tracking-normal md:text-[3rem] lg:mb-4 lg:leading-none lg:text-center lg:text-[4.25rem] lg:tracking-[-2px]',
                color: 'inherit',
                Element: 'h2',
                variant: 'h2',
              }}
            />
          </div>
          <Motion
            initial={{ opacity: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1 }}
          >
            <TypographyV3
              color="inherit"
              variant="h6"
              className="mb-8 md:mb-10 md:text-center lg:mb-14"
            >
              {t('pop_style_comparison_copy')}
            </TypographyV3>
          </Motion>
        </GridItem>
        <GridItem colEnd={{ sm: 'main' }} colStart={{ sm: 'main' }}>
          <Motion
            animate={sliderIsInView && { opacity: 1 }}
            initial={{ opacity: 0 }}
            ref={sliderRef}
            transition={{ duration: 0.5 }}
          >
            <ComparisonSlider
              items={[
                {
                  description: t('pop_style_comparison_horizon_description'),
                  desktopImage: (
                    <PopStyleComparisonItem
                      backgroundImage={{
                        src: 'blue-sky/pop/horizon_silver_compare_bg_d@3x.png',
                      }}
                      isInView={sliderIsInView}
                      maxWidthBreakpoint={1440}
                      ringImage={{
                        alt: t('pop_style_comparison_horizon_silver_alt'),
                        height: 464,
                        src: 'blue-sky/pop/horizon_silver_compare_ring@3x.png',
                        width: 464,
                      }}
                    />
                  ),
                  mobileImage: (
                    <PopStyleComparisonItem
                      backgroundImage={{
                        src: 'blue-sky/pop/horizon_silver_compare_bg_m@3x.png',
                      }}
                      isInView={sliderIsInView}
                      maxWidthBreakpoint={463}
                      ringImage={{
                        alt: t('pop_style_comparison_horizon_silver_alt'),
                        height: 321,
                        src: 'blue-sky/pop/horizon_silver_compare_ring@3x.png',
                        width: 321,
                      }}
                    />
                  ),
                  title: t('pdp_style_horizon'),
                },
                {
                  description: t('pop_style_comparison_heritage_description'),
                  desktopImage: (
                    <PopStyleComparisonItem
                      backgroundImage={{
                        src: 'blue-sky/pop/heritage_silver_compare_bg_d@3x.png',
                      }}
                      isInView={sliderIsInView}
                      maxWidthBreakpoint={1440}
                      ringImage={{
                        alt: t('pop_style_comparison_heritage_silver_alt'),
                        height: 463,
                        src: 'blue-sky/pop/heritage_silver_compare_ring@3x.png',
                        width: 468,
                      }}
                    />
                  ),
                  mobileImage: (
                    <PopStyleComparisonItem
                      backgroundImage={{
                        src: 'blue-sky/pop/heritage_silver_compare_bg_m@3x.png',
                      }}
                      isInView={sliderIsInView}
                      maxWidthBreakpoint={463}
                      ringImage={{
                        alt: t('pop_style_comparison_horizon_silver_alt'),
                        height: 316,
                        src: 'blue-sky/pop/heritage_silver_compare_ring@3x.png',
                        width: 320,
                      }}
                    />
                  ),
                  title: t('pdp_style_heritage'),
                },
              ]}
            />
          </Motion>
        </GridItem>
      </LayoutGrid>
    </section>
  );
}
